import { Box, Stack, Typography } from '@mui/material';
import type { ColDef, SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import * as HOOKS from '@siloreal:shared:hooks';

const PLATFORM_GREEN = '#6baa00';
const PARTIAL_ORANGE = '#e8710a';
const FALLBACK_STRING = '—';

export function buildOnFieldStockColumn(): ColDef {
  return {
    field: 'remaining_stock',
    headerName: HOOKS.useLocale.getState().t('temp.common.stock_in_field'),
    minWidth: 132,
    renderCell: ({ row }) => <OnFieldStockCell row={row} />,
  };
}

function OnFieldStockCell({ row }: { row: SilobagRow }) {
  const hasExtraction = resolveHasExtraction(row);
  const weightLabel = formatStockWeight(row.remaining_stock);

  const i18n = HOOKS.useLocale();
  const ACTIVE_LABEL = i18n.t('temp.common.active');
  const PARTIAL_LABEL = i18n.t('temp.common.partial_extraction');

  const statusLabel = hasExtraction ? PARTIAL_LABEL : ACTIVE_LABEL;
  const accentColor = hasExtraction ? PARTIAL_ORANGE : PLATFORM_GREEN;

  return (
    <Stack
      height="100%"
      justifyContent="center"
      onClick={(event) => event.stopPropagation()}
      sx={{ minWidth: 0, px: 1, py: 0.5 }}
    >
      <Box
        title={`${weightLabel} · ${statusLabel}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.45,
          pl: 1,
          borderLeft: `3px solid ${colorToRgba(accentColor, hasExtraction ? 0.58 : 0.46)}`,
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.25,
            letterSpacing: '0.01em',
            color: '#3A3440',
            minWidth: 0,
          }}
        >
          {weightLabel}
        </Typography>

        <Box
          component="span"
          sx={{
            alignSelf: 'flex-start',
            px: 0.65,
            py: 0.15,
            borderRadius: '4px',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.02em',
            lineHeight: 1.35,
            bgcolor: colorToRgba(accentColor, 0.14),
            color: accentColor,
            border: `1px solid ${colorToRgba(accentColor, 0.24)}`,
            boxShadow: `inset 0 1px 0 ${colorToRgba(accentColor, 0.1)}`,
          }}
        >
          {statusLabel}
        </Box>
      </Box>
    </Stack>
  );
}

function resolveHasExtraction(row: SilobagRow): boolean {
  if (row.partially_extracted) return true;

  const extracted = Number(row.total_extracted_weight ?? 0);
  return Number.isFinite(extracted) && extracted > 0;
}

function formatStockWeight(value: string | null | undefined): string {
  const numeric = parseFloat(value ?? '0');

  if (!Number.isFinite(numeric)) return FALLBACK_STRING;

  return `${numeric.toLocaleString('es-AR')} kg`;
}

function colorToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  const red = parseInt(expanded.slice(0, 2), 16);
  const green = parseInt(expanded.slice(2, 4), 16);
  const blue = parseInt(expanded.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
